import type { StationSensorAlertRuleRow } from '@/features/admin/types/opsExtended.types';
import type { IoTDataPoint } from '@/features/realtime/types/realtime.types';
import { rainfallWindowCodeFromDurationH } from '@/features/realtime/utils/stationSensorBindings';

function stripThresholdApiFields(s: IoTDataPoint): IoTDataPoint {
  const next: IoTDataPoint = { ...s };
  delete next.thresholdBreached;
  delete next.thresholdSeverity;
  delete next.thresholdValue;
  delete next.thresholdWarning;
  delete next.thresholdDanger;
  return next;
}

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}

function ruleRoutingKey(rule: StationSensorAlertRuleRow): string {
  return (rule.channel_code || rule.routing_key || '').trim();
}

function stationChannelIdSet(station: IoTDataPoint): Set<string> {
  const ids = new Set<string>();
  for (const id of station.sensorChannelIds ?? []) {
    const s = id.trim();
    if (s) ids.add(s);
  }
  const primary = station.sensorChannelId?.trim();
  if (primary) ids.add(primary);
  return ids;
}

function ruleMatchesStationChannel(
  rule: StationSensorAlertRuleRow,
  station: IoTDataPoint,
): boolean {
  const ruleChannelId = rule.channel_id?.trim();
  if (ruleChannelId) return stationChannelIdSet(station).has(ruleChannelId);

  const rk = ruleRoutingKey(rule);
  if (rk) {
    const readings = station.readingsByRoutingKey ?? {};
    if (rk in readings) return true;
    if (station.sensorChannelCode === rk) return true;
  }
  return false;
}

function ruleMatchesWindow(rule: StationSensorAlertRuleRow, station: IoTDataPoint): boolean {
  const w = (rule.window_code ?? '').trim();
  if (!w) return true;
  const rk = ruleRoutingKey(rule);
  if (rk !== 'rainfall') return w === '';
  const effective = station.lastRainfallWindowCode ?? '1h';
  return w === effective;
}

function ruleMatchesStation(rule: StationSensorAlertRuleRow, station: IoTDataPoint): boolean {
  if (String(rule.station_id) !== String(station.id)) return false;
  if (rule.enabled === false) return false;
  if (!ruleMatchesStationChannel(rule, station)) return false;
  if (!ruleMatchesWindow(rule, station)) return false;
  return true;
}

function readingForRule(station: IoTDataPoint, rule: StationSensorAlertRuleRow): number {
  const chId = rule.channel_id?.trim();
  if (chId && station.readingsByChannelId?.[chId] != null) {
    return station.readingsByChannelId[chId]!;
  }
  const rk = ruleRoutingKey(rule);
  if (rk && station.readingsByRoutingKey?.[rk] != null) {
    return station.readingsByRoutingKey[rk]!;
  }
  return station.value;
}

function levelsFromRule(rule: StationSensorAlertRuleRow): { warning?: number; danger?: number } {
  const warn = num(rule.warn_threshold);
  const info = num(rule.info_threshold);
  const danger = num(rule.danger_threshold);
  const warning = warn ?? info;
  const out: { warning?: number; danger?: number } = {};
  if (warning !== undefined) out.warning = warning;
  if (danger !== undefined) out.danger = danger;
  return out;
}

function evaluateBreach(
  value: number,
  levels: { warning?: number; danger?: number },
): {
  breached: boolean;
  severity?: 'warning' | 'danger';
  thresholdWarning?: number;
  thresholdDanger?: number;
  thresholdValue?: number;
} {
  const { warning, danger } = levels;
  if (danger !== undefined && value >= danger) {
    return {
      breached: true,
      severity: 'danger',
      thresholdWarning: warning,
      thresholdDanger: danger,
      thresholdValue: danger,
    };
  }
  if (warning !== undefined && value >= warning) {
    return {
      breached: true,
      severity: 'warning',
      thresholdWarning: warning,
      thresholdDanger: danger,
      thresholdValue: danger ?? warning,
    };
  }
  return {
    breached: false,
    thresholdWarning: warning,
    thresholdDanger: danger,
    thresholdValue: danger ?? warning,
  };
}

function breachRank(ev: ReturnType<typeof evaluateBreach>): number {
  if (!ev.breached) return 0;
  return ev.severity === 'danger' ? 2 : 1;
}

function pickWorstEvaluation(
  candidates: ReturnType<typeof evaluateBreach>[],
): ReturnType<typeof evaluateBreach> | null {
  if (candidates.length === 0) return null;
  const breachedOnes = candidates.filter((c) => c.breached);
  const pool = breachedOnes.length > 0 ? breachedOnes : candidates;
  return pool.reduce((best, ev) => {
    const br = breachRank(ev);
    const bb = breachRank(best);
    if (br > bb) return ev;
    if (br === bb && br > 0 && ev.severity === 'danger' && best.severity !== 'danger') return ev;
    return best;
  });
}

/**
 * Gộp trạm với rule GET /api/alerts/thresholds (station_id, channel_id, window_code).
 * Nhiều rule: ưu tiên breach nặng nhất (danger > warning).
 */
export function applyAlertThresholdsToStations(
  stations: IoTDataPoint[],
  thresholdRows: StationSensorAlertRuleRow[],
): IoTDataPoint[] {
  if (stations.length === 0) return stations;

  return stations.map((station) => {
    const matching = thresholdRows.filter((r) => ruleMatchesStation(r, station));
    if (matching.length === 0) {
      return stripThresholdApiFields(station);
    }

    const candidates: ReturnType<typeof evaluateBreach>[] = [];
    for (const rule of matching) {
      const levels = levelsFromRule(rule);
      if (levels.warning === undefined && levels.danger === undefined) continue;
      candidates.push(evaluateBreach(readingForRule(station, rule), levels));
    }

    const best = pickWorstEvaluation(candidates);
    if (!best) {
      return stripThresholdApiFields(station);
    }

    const { breached, severity, thresholdWarning, thresholdDanger, thresholdValue } = best;
    const next: IoTDataPoint = { ...station, thresholdBreached: breached };
    if (severity) next.thresholdSeverity = severity;
    else delete next.thresholdSeverity;
    if (thresholdWarning !== undefined) next.thresholdWarning = thresholdWarning;
    else delete next.thresholdWarning;
    if (thresholdDanger !== undefined) next.thresholdDanger = thresholdDanger;
    else delete next.thresholdDanger;
    if (thresholdValue !== undefined) next.thresholdValue = thresholdValue;
    else delete next.thresholdValue;
    return next;
  });
}

export interface StationChannelBreachLine {
  channelLabel: string;
  routingKey: string;
  severity: 'warning' | 'danger';
}

/** Các kênh đang vượt ngưỡng (một trạm) — dùng mô tả chuông, không gộp severity. */
export function listThresholdBreachesForStation(
  station: IoTDataPoint,
  thresholdRows: StationSensorAlertRuleRow[],
): StationChannelBreachLine[] {
  const matching = thresholdRows.filter((r) => ruleMatchesStation(r, station));
  const byKey = new Map<string, StationChannelBreachLine>();

  for (const rule of matching) {
    const levels = levelsFromRule(rule);
    if (levels.warning === undefined && levels.danger === undefined) continue;
    const ev = evaluateBreach(readingForRule(station, rule), levels);
    if (!ev.breached || !ev.severity) continue;

    const rk = ruleRoutingKey(rule) || rule.channel_id?.trim() || 'sensor';
    const label =
      (rule.channel_name && String(rule.channel_name).trim()) || rule.channel_code?.trim() || rk;
    const prev = byKey.get(rk);
    if (!prev || breachRank(ev) > breachRank({ breached: true, severity: prev.severity })) {
      byKey.set(rk, { channelLabel: label, routingKey: rk, severity: ev.severity });
    }
  }

  return [...byKey.values()];
}

/** Rule khớp trạm + kênh + cửa sổ mưa (nếu có). */
export function matchingThresholdRulesForStation(
  station: IoTDataPoint,
  thresholdRows: StationSensorAlertRuleRow[],
): StationSensorAlertRuleRow[] {
  return thresholdRows.filter((r) => ruleMatchesStation(r, station));
}

/** Rule gắn trạm cho dropdown metric (không lọc theo cửa sổ mưa hiện tại). */
export function matchingThresholdRulesForStationOptions(
  station: IoTDataPoint,
  thresholdRows: StationSensorAlertRuleRow[],
): StationSensorAlertRuleRow[] {
  return thresholdRows.filter(
    (r) =>
      String(r.station_id) === String(station.id) &&
      r.enabled !== false &&
      ruleMatchesStationChannel(r, station),
  );
}

/** Rule ưu tiên cho một `routing_key` (mưa: khớp `lastRainfallWindowCode`). */
export function primaryThresholdRuleForRoutingKey(
  station: IoTDataPoint,
  thresholdRows: StationSensorAlertRuleRow[],
  routingKey: string,
): StationSensorAlertRuleRow | null {
  const rk = routingKey.trim().toLowerCase();
  const matching = matchingThresholdRulesForStation(station, thresholdRows).filter(
    (r) => ruleRoutingKey(r).toLowerCase() === rk,
  );
  if (matching.length === 0) return null;
  if (rk === 'rainfall') {
    const preferred = station.lastRainfallWindowCode ?? '1h';
    const hit = matching.find((r) => (r.window_code ?? '').trim() === preferred);
    if (hit) return hit;
  }
  const noWindow = matching.find((r) => !(r.window_code ?? '').trim());
  return noWindow ?? matching[0] ?? null;
}

export function evaluateRuleBreachForStation(
  station: IoTDataPoint,
  rule: StationSensorAlertRuleRow,
): ReturnType<typeof evaluateBreach> {
  return evaluateBreach(readingForRule(station, rule), levelsFromRule(rule));
}

/** Đường ngưỡng biểu đồ lịch sử — mưa có thể chọn cửa sổ 1h/24h riêng. */
export function resolveThresholdLinesForMetricOption(
  station: IoTDataPoint,
  thresholdRows: StationSensorAlertRuleRow[],
  routingKey: string,
  windowCode?: string,
): { warning?: number; danger?: number } {
  const rk = routingKey.trim().toLowerCase();
  const w = windowCode?.trim();
  if (rk === 'rainfall' && w) {
    const rule = matchingThresholdRulesForStation(station, thresholdRows).find(
      (r) => ruleRoutingKey(r) === 'rainfall' && (r.window_code ?? '').trim() === w,
    );
    if (!rule) return {};
    const levels = levelsFromRule(rule);
    return { warning: levels.warning, danger: levels.danger };
  }
  const rule = primaryThresholdRuleForRoutingKey(station, thresholdRows, routingKey);
  if (!rule) return {};
  const ev = evaluateRuleBreachForStation(station, rule);
  return { warning: ev.thresholdWarning, danger: ev.thresholdDanger };
}

/** Exported for tests — map duration_h → window rule code. */
export { rainfallWindowCodeFromDurationH };
